'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { i18n } from '@/config/i18n';
import { DropdownArrowIcon } from '@/components/icons';
import { Badge } from '@/components/ui-components';
// import { Skeleton } from '../Skeleton';

export interface DropdownOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

export interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  buttonLabel?: string|React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  searchable?: boolean;
  loading?: boolean;
  className?: string;
  icon?: React.ReactNode;
  title?: string;
  variant?: 'form' | 'navbar';
  useFixedPosition?: boolean; // Use fixed positioning for modals
  helperIcon?: React.ReactNode;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
  suggestedValue?: string; // Value code to visually highlight as suggested (doesn't auto-select)
}

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.label<{ $required?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.gray.darkText};
  margin-bottom: 6px;
`;

const RequiredMark = styled.span`
  margin-left: 2px;
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownButton = styled.button<{ 
  $isOpen: boolean; 
  $hasValue: boolean; 
  $disabled?: boolean; 
  $error?: boolean;
  $variant: 'form' | 'navbar';
  $size?: 'sm' | 'md';
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ $variant }) => ($variant === 'navbar' ? 'auto' : '100%')};
  gap: 8px;
  font-size: 14px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  padding: ${({ $variant, $size }) => {
    if ($variant === 'navbar') return '8px 12px';
    if ($size === 'sm') return '2px 8px';
    return '7px 12px';
  }};
  border-radius: ${({ $size }) => ($size === 'sm' ? '4px' : '6px')};
  transition: all 0.2s;
  min-height: ${({ $variant, $size }) => {
    if ($variant === 'navbar') return 'auto';
    if ($size === 'sm') return '32px';
    return '36px';
  }};
  text-align: left;
  min-width: ${({ $variant }) => ($variant === 'navbar' ? '150px' : 'auto')};
  font-family: 'Open Sans', sans-serif;
  border-color: ${theme.colors.border.primary};

  /* Form variant styles */
  ${({ $variant, $disabled, $error, $isOpen, $hasValue }) =>
    $variant === 'form'
      ? `
    background: ${$disabled ? theme.colors.gray[100] : theme.colors.white};
    border: 1px solid ${$error ? theme.colors.status.errorDark : $isOpen ? theme.colors.primary.accent : theme.colors.border.primary};
    color: ${$disabled ? theme.colors.gray[400] : $hasValue ? theme.colors.gray[800] : theme.colors.gray[500]};

    &:hover {
      ${!$disabled && !$error ? `border-color: ${theme.colors.primary.accent};` : 'border-color: inherit;'}
    }

    &:focus {
      outline: none;
      border-color: ${$error ? theme.colors.status.errorDark : theme.colors.primary.accent};
      box-shadow: ${$error ? theme.colors.overlay.focusError : theme.colors.overlay.focus};
    }

    .dropdown-icon {
      transition: transform 0.2s;
      color: ${theme.colors.gray[500]};
      flex-shrink: 0;
    }

    &[data-open='true'] .dropdown-icon {
      transform: rotate(180deg);
      color: ${theme.colors.primary.accent};
    }
  `
      : `
    /* Navbar variant styles */
    background: transparent;
    border: 1px solid ${theme.colors.overlay.white20};
    color: ${theme.colors.white};

    &:hover {
      background-color: ${theme.colors.overlay.hover};
      border-color: ${theme.colors.overlay.white30};
    }

    &:focus {
      outline: none;
      border-color: ${theme.colors.status.info};
    }

    .dropdown-icon {
      margin-left: auto;
      transition: transform 0.2s;
      color: ${theme.colors.white};
      flex-shrink: 0;
    }

    &[data-open='true'] .dropdown-icon {
      transform: rotate(180deg);
    }
  `}
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean; $variant: 'form' | 'navbar'; $isFixed?: boolean }>`
  position: ${({ $isFixed }) => ($isFixed ? 'fixed' : 'absolute')};
  top: ${({ $isFixed }) => ($isFixed ? 'auto' : 'calc(100% + 4px)')};
  left: ${({ $isFixed }) => ($isFixed ? 'auto' : '0')};
  right: ${({ $isFixed }) => ($isFixed ? 'auto' : '0')};
  width: ${({ $isFixed }) => ($isFixed ? 'auto' : '100%')};
  border-radius: 6px;
  z-index: 1;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transform: ${({ $isFixed, $isOpen }) => 
    $isFixed ? 'none' : `translateY(${$isOpen ? 0 : -10}px)`
  };
  transition: ${({ $isFixed }) => ($isFixed ? 'opacity 0.15s, visibility 0.15s' : 'all 0.2s')};
  max-height: 300px;
  overflow-y: auto;
  min-width: ${({ $isFixed }) => ($isFixed ? '180px' : 'auto')};

  /* Form variant styles */
  ${({ $variant }) =>
    $variant === 'form'
      ? `
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.border.tertiary};
    box-shadow: ${theme.shadows.dropdown};

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${theme.colors.overlay.black05};
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.border.tertiary};
      border-radius: 3px;

      &:hover {
        background: ${theme.colors.gray[400]};
      }
    }
  `
      : `
    /* Navbar variant styles */
    background-color: ${theme.colors.gray[800]};
    border: 1px solid ${theme.colors.overlay.white20};
    box-shadow: ${theme.shadows.dropdownDark};

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${theme.colors.overlay.black10};
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.overlay.white30};
      border-radius: 3px;
    }
  `}
`;

const SearchInputWrapper = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${theme.colors.gray[200]};
  position: sticky;
  top: 0;
  background: ${theme.colors.white};
  z-index: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${theme.colors.border.tertiary};
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: ${theme.colors.gray[500]};
  }

  &:focus {
    border-color: ${theme.colors.primary.accent};
    box-shadow: ${theme.colors.overlay.focus};
  }
`;

const DropdownItem = styled.button<{ $isSelected: boolean; $variant: 'form' | 'navbar'; $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  font-size: 14px;
   font-family: 'Open Sans', sans-serif;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s;
  text-align: left;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  /* Form variant styles */
  ${({ $variant, $isSelected }) =>
    $variant === 'form'
      ? `
    background: ${$isSelected ? theme.colors.special.orangeTint : 'transparent'};
    color: ${$isSelected ? theme.colors.primary.accent : theme.colors.gray[800]};

    &:hover {
      background-color: ${$isSelected ? theme.colors.special.orangeTint : theme.colors.gray[100]};
    }
  `
      : `
    /* Navbar variant styles */
    background: ${$isSelected ? theme.colors.overlay.selected : 'transparent'};
    color: ${theme.colors.white};

    &:hover {
      background-color: ${theme.colors.overlay.hover};
    }
  `}

  &:first-of-type {
    border-radius: 6px 6px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 6px 6px;
  }
`;

// Badge components moved to @/components/ui-components/Badge

const ErrorText = styled.span`
  display: block;
  font-size: 13px;
  color: ${theme.colors.status.errorDark};
  margin-top: 4px;
`;

const NoResults = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${theme.colors.gray[500]};
  font-size: 14px;
`;


/**
 * Dropdown Component
 * 
 * A reusable dropdown select component with customizable options,
 * support for labels, errors, search functionality, and accessibility
 * 
 * @component
 * @example
 * ```tsx
 * const options = [
 *   { label: 'Option 1', value: '1' },
 *   { label: 'Option 2', value: '2' }
 * ];
 * 
 * <Dropdown 
 *   label="Select an option"
 *   options={options}
 *   value="1"
 *   onChange={(value) => console.log(value)}
 *   required
 *   searchable
 * />
 * ```
 */
export const Dropdown: React.FC<DropdownProps> = ({
  buttonLabel,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  required = false,
  disabled = false,
  error,
  searchable = false,
  // loading removed (unused)
  className,
  icon,
  title,
  variant = 'form',
  useFixedPosition = false,
  helperIcon,
  size = 'md',
  style,
  suggestedValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const safeOptions = options || [];
  const selectedOption = safeOptions.find((opt) => opt.value === value);
  const suggestedOption = suggestedValue ? safeOptions.find((opt) => opt.value === suggestedValue) : undefined;

  // Filter options based on search term
  const filteredOptions = searchable && searchTerm
    ? safeOptions.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : safeOptions;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update menu position for fixed positioning
  const updateMenuPosition = useCallback(() => {
    if (!useFixedPosition || !buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight || 300;
    const buttonWidth = rect.width;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Check if dropdown would overflow bottom of viewport
    const shouldShowAbove = spaceBelow < menuHeight + 10 && spaceAbove > menuHeight;
    
    // Calculate left position - align with button, but ensure it doesn't overflow viewport
    const estimatedMenuWidth = Math.max(buttonWidth, 180);
    let left = rect.left;
    if (left + estimatedMenuWidth > viewportWidth) {
      left = viewportWidth - estimatedMenuWidth - 10; // 10px padding from edge
    }
    if (left < 10) {
      left = 10; // 10px padding from edge
    }
    
    setMenuPosition({
      top: shouldShowAbove ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: left,
      width: buttonWidth,
    });
    setIsPositionCalculated(true);
  }, [useFixedPosition]);

  // Handle fixed positioning with event listeners
  useEffect(() => {
    if (!useFixedPosition || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    // Find scrollable parent containers
    const findScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
      const parents: HTMLElement[] = [];
      let current = element?.parentElement;
      
      while (current) {
        const style = window.getComputedStyle(current);
        if (style.overflow === 'auto' || style.overflow === 'scroll' || 
            style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parents.push(current);
        }
        current = current.parentElement;
      }
      
      return parents;
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Update position on window scroll
    window.addEventListener('scroll', updateMenuPosition, true);
    
    // Update position on window resize
    window.addEventListener('resize', updateMenuPosition);
    
    // Find and listen to scrollable parent containers
    const scrollableParents = findScrollableParents(buttonRef.current);
    scrollableParents.forEach(parent => {
      parent.addEventListener('scroll', updateMenuPosition, true);
    });
    
    // Initial position update - menu should already be positioned from handleToggle
    // But update it once rendered to get accurate height
    const timeoutId = setTimeout(() => {
      if (isPositionCalculated) {
        updateMenuPosition();
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
      scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', updateMenuPosition, true);
      });
    };

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
      scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', updateMenuPosition, true);
      });
    };
  }, [isOpen, useFixedPosition, updateMenuPosition, isPositionCalculated]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    if (!disabled) {
      onChange?.(optionValue);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen && useFixedPosition) {
        // Calculate position before opening for fixed positioning
        setIsPositionCalculated(false);
        // Use requestAnimationFrame to ensure button is rendered
        requestAnimationFrame(() => {
          if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const estimatedMenuHeight = 300;
            const buttonWidth = rect.width;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            
            const shouldShowAbove = spaceBelow < estimatedMenuHeight + 10 && spaceAbove > estimatedMenuHeight;
            const estimatedMenuWidth = Math.max(buttonWidth, 180);
            let left = rect.left;
            if (left + estimatedMenuWidth > viewportWidth) {
              left = viewportWidth - estimatedMenuWidth - 10;
            }
            if (left < 10) {
              left = 10;
            }
            
            setMenuPosition({
              top: shouldShowAbove ? rect.top - estimatedMenuHeight - 4 : rect.bottom + 4,
              left: left,
              width: buttonWidth,
            });
            setIsPositionCalculated(true);
            setIsOpen(true);
          }
        });
      } else {
        setIsOpen(!isOpen);
        if (isOpen) {
          setSearchTerm('');
          if (useFixedPosition) {
            setIsPositionCalculated(false);
            setMenuPosition(null);
          }
        }
      }
    }
  };

  return (
    <DropdownContainer ref={dropdownRef} className={className}>
      {label && variant === 'form' && (
        <Label $required={required} $disabled={disabled} htmlFor="dropdown-button">
          {label}
          {required && <RequiredMark>*</RequiredMark>}
          {helperIcon}
        </Label>
      )}

      <DropdownWrapper>
        <DropdownButton
          ref={buttonRef}
          id="dropdown-button"
          onClick={handleToggle}
          data-open={isOpen}
          $isOpen={isOpen}
          $hasValue={!!selectedOption || !!suggestedOption}
          $disabled={disabled}
          $error={!!error}
          $variant={variant}
          $size={size}
          disabled={disabled}
          type="button"
          title={title || (selectedOption?.label ?? suggestedOption?.label ?? placeholder)}
          aria-label={label || placeholder}
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-haspopup="listbox"
          style={style}
        >
          <LabelWrapper>
          {icon && icon}
          {buttonLabel ?? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              <span>
                {selectedOption?.label || suggestedOption?.label || placeholder}
              </span>
              {variant === 'form' && suggestedOption && selectedOption?.value === suggestedValue && (
                <Badge variant="success" style={{ marginLeft: 'auto' }}>{i18n.common.badges.suggested}</Badge>
              )}
            </span>
          )}
          </LabelWrapper>
         
            <DropdownArrowIcon 
              size={18} 
              className="dropdown-icon"
              color={isOpen ? (variant === 'form' ? theme.colors.primary.accent : theme.colors.white) : (variant === 'form' ? theme.colors.gray[500] : theme.colors.white)} 
            />
     
        </DropdownButton>

        <DropdownMenu 
          ref={menuRef}
          $isOpen={isOpen && (!useFixedPosition || isPositionCalculated)} 
          $variant={variant} 
          $isFixed={useFixedPosition}
          style={useFixedPosition && menuPosition ? { 
            top: `${menuPosition.top}px`, 
            left: `${menuPosition.left}px`,
            width: menuPosition.width > 0 ? `${menuPosition.width}px` : 'auto'
          } : undefined}
          role="listbox"
        >
          {searchable && variant === 'form' && (
            <SearchInputWrapper>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </SearchInputWrapper>
          )}

          {filteredOptions?.length > 0 ? (
            filteredOptions.map((option) => {
              const isSuggested = suggestedValue && option.value === suggestedValue && value !== suggestedValue;
              return (
                <DropdownItem
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  $isSelected={option.value === value}
                  $variant={variant}
                  $disabled={false}
                  type="button"
                  role="option"
                  aria-selected={option.value === value ? 'true' : 'false'}
                >
                  {option.icon && <span>{option.icon}</span>}
                  <span>{option.label}</span>
                  {isSuggested && (
                    <Badge variant="success" title={i18n.common.badges.suggestedTooltip}>
                      {i18n.common.badges.suggested}
                    </Badge>
                  )}
                </DropdownItem>
              );
            })
          ) : (
            searchable && variant === 'form' && <NoResults>No results found</NoResults>
          )}
        </DropdownMenu>
      </DropdownWrapper>

      {error && variant === 'form' && <ErrorText role="alert">{error}</ErrorText>}
    </DropdownContainer>
  );
};

export default Dropdown;

